import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { pageHead } from "@/lib/routeHead";
import { videoEmbed, videoPageQuery, videoSchema, videoThumb } from "@/lib/videoPages";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

const BASE = "https://www.rexandsonsrvs.com";

export const Route = createFileRoute("/videos/$slug")({
  loader: async ({ params, context }) => {
    const v = await context.queryClient.ensureQueryData(videoPageQuery(params.slug));
    if (!v) throw notFound();
    return v;
  },
  head: ({ loaderData: v, params }) => {
    const path = `/videos/${params.slug}`;
    const head = pageHead({
      title: v?.title ?? "RV Video",
      description: v?.meta_description || v?.summary || "RV video from Rex & Sons RVs in Castle Hayne, NC.",
      path,
      type: "video.other",
      ...(v ? { image: videoThumb(v.youtube_id) } : {}),
    });
    if (!v) return { ...head, meta: [...head.meta, { name: "robots", content: "noindex" }] };
    const crumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      name: "Breadcrumbs",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BASE },
        { "@type": "ListItem", position: 2, name: "Videos", item: `${BASE}/videos` },
        { "@type": "ListItem", position: 3, name: v.title, item: `${BASE}${path}` },
      ],
    };
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(videoSchema(v, `${BASE}${path}`)) },
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
      ],
    };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="container py-16 text-center">
        <h1 className="font-heading text-2xl font-bold">Video not found</h1>
        <Link to="/videos" className="mt-4 inline-block text-primary underline">See all videos</Link>
      </div>
    </Layout>
  ),
  component: VideoPageView,
});

function VideoPageView() {
  const { slug } = Route.useParams();
  const { data: v } = useSuspenseQuery(videoPageQuery(slug));
  if (!v) return null;
  return (
    <Layout>
      <article className="container max-w-4xl py-8">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link> /{" "}
          <Link to="/videos" className="hover:text-primary">Videos</Link> / <span className="text-foreground">{v.title}</span>
        </nav>
        <h1 className="font-heading text-2xl font-bold text-foreground md:text-4xl">{v.title}</h1>
        <div className="mt-5 aspect-video w-full overflow-hidden rounded-xl bg-muted">
          <iframe
            src={videoEmbed(v.youtube_id)}
            title={v.title}
            className="h-full w-full"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {v.summary && <p className="mt-5 text-lg text-muted-foreground">{v.summary}</p>}
        <div className="prose prose-neutral mt-6 max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: sanitizeHtml(v.html_content) }} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="min-h-11 rounded-full"><Link to="/rv-search">Browse Inventory</Link></Button>
          <Button asChild variant="outline" className="min-h-11 rounded-full"><Link to="/contact">Contact Us</Link></Button>
          <Button asChild variant="ghost" className="min-h-11 rounded-full"><Link to="/videos">More Videos</Link></Button>
        </div>
      </article>
    </Layout>
  );
}
